import React from 'react';
import CareerPathwayDropdown from './CareerPathwayDropdown';
import CareerPathwayDataPoints from '@/components/DesignSystem/Icons/svgs/CareerPathwayDataPoints';
import ThreeDotLoader from './ThreeDotLoader';
import {
  Degree,
  Occupation,
  Subject,
} from '@/components/DesignSystem/Subsections/SalaryCalculator/types';
import { Category } from '@/types/Mobius';
import tagularEvent from '@/utils/tagular/tagularEvent';

export interface CareerPathPlannerWidgetProps {
  className?: string;
  currentEducationOptions: Degree[];
  currentRoleOptions: Occupation[];
  targetRoleOptions: Occupation[];
  selectedCurrentEducation?: Degree;
  selectedFieldOfStudy?: Subject;
  selectedCurrentRole?: Occupation;
  selectedTargetRole?: Occupation;
  onSubmit: (data: any) => void;
  onCurrentEducationChange: (data: Degree) => void;
  onFieldOfStudyChange: (data: Subject) => void;
  onCurrentRoleChange: (data: Occupation) => void;
  onTargetRoleChange: (data: Occupation) => void;
  editOpen: boolean;
  loading: boolean;
  correlationId: string;
  viewCorrelationId: string;
  categories: Category[];
}

const CareerPathPlannerWidget = ({
  className,
  currentEducationOptions,
  currentRoleOptions,
  targetRoleOptions,
  selectedCurrentEducation,
  selectedFieldOfStudy,
  selectedCurrentRole,
  selectedTargetRole,
  onSubmit,
  onCurrentEducationChange,
  onFieldOfStudyChange,
  onCurrentRoleChange,
  onTargetRoleChange,
  editOpen,
  loading,
  correlationId,
  viewCorrelationId,
  categories,
}: CareerPathPlannerWidgetProps) => {
  const isFormComplete =
    selectedCurrentEducation &&
    selectedFieldOfStudy &&
    selectedCurrentRole &&
    selectedTargetRole;

  const handleSubmit = () => {
    if (isFormComplete && onSubmit) {
      onSubmit({
        currentEducation: selectedCurrentEducation,
        fieldOfStudy: selectedFieldOfStudy,
        currentRole: selectedCurrentRole,
        targetRole: selectedTargetRole,
      });
    }
  };

  const isLoading = loading;

  return (
    <div
      className={`bg-gray-50 border border-gray-200 max-w-4xl mx-auto rounded-xl p-3 md:p-5 not-prose ${className}`}
    >
      {/* Top Section */}
      <div className='flex-col'>
        <div className='flex flex-col md:flex-row gap-8 md:max-h-52'>
          <div className='flex flex-col md:w-1/2'>
            <span className='text-xl md:text-2xl font-bold pt-2 md:pt-5 pb-2 text-black'>
              Your Career Path Planner
            </span>
            <span className='text-base md:text-lg text-gray-500'>
              Uncover the path to the career you&apos;ve always wanted.
              We&apos;ll show you the steps to making your dreams a reality.
            </span>
          </div>
          <div className='md:w-1/2 flex object-contain'>
            <CareerPathwayDataPoints />
          </div>
        </div>
        {isLoading && <ThreeDotLoader />}
      </div>
      {/* Bottom Section */}
      {!isLoading && (
        <div className='flex flex-col gap-5'>
          <div className='italic text-base md:text-lg font-bold text-black'>
            Start by entering your education and career goals:
          </div>
          {/* Dropdowns */}
          <div className='grid grid-cols-2  md:grid-cols-2 gap-2 text-xs md:text-sm'>
            <CareerPathwayDropdown
              label='Current Education'
              options={currentEducationOptions}
              selectedOption={selectedCurrentEducation || null}
              onOptionChange={onCurrentEducationChange}
              placeholder='Select current education'
              getOptionValue={(option) => option.id?.toString() || ''}
              getOptionDisplay={(option) => option.name || ''}
              isEditable={true}
              correlationId={correlationId}
              viewCorrelationId={viewCorrelationId}
            />
            <CareerPathwayDropdown
              label='Field of Study'
              options={categories}
              selectedOption={selectedFieldOfStudy || null}
              onOptionChange={onFieldOfStudyChange}
              placeholder='Select field of study'
              getOptionValue={(option) => option.id?.toString() || ''}
              getOptionDisplay={(option) => option.name || ''}
              isEditable={true}
              correlationId={correlationId}
              viewCorrelationId={viewCorrelationId}
            />
            <CareerPathwayDropdown
              label='Current Role'
              options={currentRoleOptions}
              selectedOption={selectedCurrentRole || null}
              onOptionChange={onCurrentRoleChange}
              placeholder='Select current role'
              getOptionValue={(option) => option.title || ''}
              getOptionDisplay={(option) => option.title || ''}
              isEditable={true}
              correlationId={correlationId}
              viewCorrelationId={viewCorrelationId}
            />
            <CareerPathwayDropdown
              label='Target Role'
              options={targetRoleOptions}
              selectedOption={selectedTargetRole || null}
              onOptionChange={onTargetRoleChange}
              placeholder='Select target role'
              getOptionValue={(option) => option.title || ''}
              getOptionDisplay={(option) => option.title || ''}
              isEditable={true}
              correlationId={correlationId}
              viewCorrelationId={viewCorrelationId}
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!isFormComplete}
            className={`max-w-fit h-11 px-4 py-2 rounded-lg font-semibold text-white text-sm transition-all duration-200 bg-secondary-700 hover:bg-secondary-800 hover:shadow-lg`}
          >
            Discover My Path
          </button>
        </div>
      )}
    </div>
  );
};

export default CareerPathPlannerWidget;
